import React, { useState } from 'react';
import { BookOpen, MapPin, Award, ExternalLink, CheckCircle2 } from 'lucide-react';

const Schemes = () => {
    const [activeTab, setActiveTab] = useState('central');

    // Default Data
    const defaultSchemes = {
        central: [
            {
                id: 1,
                title: "Pradhan Mantri Kisan Samman Nidhi (PM-KISAN)",
                description: "Income support of ₹6,000 per year in three equal installments to all land-holding farmer families.",
                benefits: ["Direct cash transfer", "₹6,000 per year", "No intermediaries"],
                eligibility: "All land-holding farmer families",
                link: "https://pmkisan.gov.in/"
            },
            {
                id: 2,
                title: "Pradhan Mantri Fasal Bima Yojana (PMFBY)",
                description: "A comprehensive crop insurance scheme to provide insurance coverage and financial support to farmers in the event of failure of any of the notified crop as a result of natural calamities.",
                benefits: ["Lowest premium rates", "Full coverage of localized calamities", "Post-harvest losses coverage"],
                eligibility: "Farmers growing notified crops",
                link: "https://pmfby.gov.in/"
            },
            {
                id: 3,
                title: "Soil Health Card Scheme",
                description: "Assist farmers in deciding the dose of fertilizers application on soil test basis.",
                benefits: ["Monitors soil health", "Reduces cultivation cost", "Increases productivity"],
                eligibility: "All farmers",
                link: "https://soilhealth.dac.gov.in/"
            }
        ],
        state: [
            {
                id: 1,
                title: "Rythu Bandhu (Telangana)",
                description: "Investment Support Scheme for farmers to purchase inputs like seeds, fertilizers, pesticides.",
                benefits: ["₹5,000 per acre per season", "Direct Benefit Transfer", "Twice a year"],
                eligibility: "Land owning farmers in Telangana",
                link: "#"
            },
            {
                id: 2,
                title: "Kalia Scheme (Odisha)",
                description: "Krushak Assistance for Livelihood and Income Augmentation for farming and small farmers.",
                benefits: ["Financial assistance", "Insurance cover", "Interest-free crop loan"],
                eligibility: "Small and marginal farmers",
                link: "#"
            }
        ],
        other: [
            {
                id: 1,
                title: "NABARD Subsidy Schemes",
                description: "Various subsidies for dairy farming, organic farming, and cold storage infrastructure.",
                benefits: ["Capital subsidy", "Low interest loans", "Technological support"],
                eligibility: "Rural entrepreneurs, Farmers",
                link: "https://www.nabard.org/"
            },
            {
                id: 2,
                title: "Kisan Credit Card (KCC)",
                description: "Provides adequate and timely credit support from the banking system to farmers for their cultivation and other needs.",
                benefits: ["Flexible credit", "Low interest rate (4% with prompt repayment)", "ATM enabled card"],
                eligibility: "All farmers, Tenant farmers",
                link: "#"
            }
        ]
    };

    // Initialize state from local storage or default
    const [schemesData] = useState(() => {
        try {
            const stored = localStorage.getItem('schemes');
            if (stored) {
                return JSON.parse(stored);
            }
            // If not stored, initialize it
            localStorage.setItem('schemes', JSON.stringify(defaultSchemes));
            return defaultSchemes;
        } catch (e) {
            return defaultSchemes;
        }
    });

    const currentSchemes = schemesData[activeTab] || [];

    return (
        <div className="min-h-screen bg-slate-50/50">
            {/* Hero Section */}
            <div className="bg-emerald-900 relative overflow-hidden py-16 sm:py-24">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1625246333195-58197bd47f26?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900 to-transparent"></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center p-2 bg-emerald-800/50 backdrop-blur-sm rounded-xl mb-6 border border-emerald-700">
                        <BookOpen className="w-5 h-5 text-emerald-400 mr-2" />
                        <span className="text-emerald-100 font-medium text-sm">Empowering Agriculture</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
                        Government Schemes <br className="hidden md:block" />
                        <span className="text-emerald-400">& Initiatives</span>
                    </h1>
                    <p className="text-lg text-emerald-100 max-w-2xl mx-auto leading-relaxed">
                        Explore beneficial schemes from Central and State governments designed to support farmers financially and technically.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10 pb-20">

                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow-xl p-2 mb-10 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                        onClick={() => setActiveTab('central')}
                        className={`flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center space-x-2 transition-all duration-200 ${activeTab === 'central' ? 'bg-emerald-600 text-white shadow-lg' : 'hover:bg-emerald-50 text-slate-600'}`}
                    >
                        <Award className="w-5 h-5" />
                        <span>Central Govt Schemes</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('state')}
                        className={`flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center space-x-2 transition-all duration-200 ${activeTab === 'state' ? 'bg-emerald-600 text-white shadow-lg' : 'hover:bg-emerald-50 text-slate-600'}`}
                    >
                        <MapPin className="w-5 h-5" />
                        <span>State Govt Schemes</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('other')}
                        className={`flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center space-x-2 transition-all duration-200 ${activeTab === 'other' ? 'bg-emerald-600 text-white shadow-lg' : 'hover:bg-emerald-50 text-slate-600'}`}
                    >
                        <BookOpen className="w-5 h-5" />
                        <span>Other Programs</span>
                    </button>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in-up">
                    {currentSchemes.map((scheme) => (
                        <div key={scheme.id} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-emerald-200 group flex flex-col">
                            <div className="mb-6">
                                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Award className="w-7 h-7 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 leading-snug group-hover:text-emerald-700 transition-colors">
                                    {scheme.title}
                                </h3>
                            </div>

                            <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                                {scheme.description}
                            </p>

                            <div className="space-y-4 mb-8">
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Benefits</h4>
                                    <ul className="space-y-1">
                                        {scheme.benefits.map((benefit, i) => (
                                            <li key={i} className="flex items-start text-sm text-slate-700">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                                                <span>{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Eligibility</h4>
                                    <p className="text-sm font-medium text-slate-800">{scheme.eligibility}</p>
                                </div>
                            </div>

                            {scheme.link && scheme.link !== '#' ? (
                                <a
                                    href={scheme.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-3.5 px-4 bg-slate-50 hover:bg-emerald-600 text-slate-700 hover:text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 group/btn"
                                >
                                    <span>Learn More from Official</span>
                                    <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    disabled
                                    className="w-full py-3.5 px-4 bg-slate-50 text-slate-400 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 cursor-not-allowed"
                                >
                                    <span>Learn More from Official</span>
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {currentSchemes.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-slate-100">
                        <p className="text-slate-400 font-medium">No active schemes found in this category currently.</p>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Schemes;

